import type { Resource } from '__types__/v1/models';
import { Image } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buttonVariants } from 'v5/design-system/Button';
import { ImagePreview } from 'v5/design-system/ImagePreview';
import { MediaMoreOverlay } from 'v5/design-system/MediaBadges';
import { Skeleton } from 'v5/design-system/Skeleton';
import {
  PictureStrip,
  PictureStripMedia,
  PictureStripSquare,
  type StripPicture
} from 'v5/patterns/collection/PictureStrip';
import { cn } from 'v5/platform/dom/cn';
import { resolvePinMedia } from 'v5/patterns/widgets/MediaCarousel/mediaCarousel.resolver';
import { resourceRow } from './resourceRow';

/** Squares a row shows where it has the room (from 760px), and in a narrower one (from 480px). */
const ROW_WIDE = 5;
const ROW_NARROW = 3;
const TILE_STRIP = 4;

/** The result's first pictures, as the search sends them, and how many it has in all. */
function picturesOf(resource: Resource) {
  const pictures: StripPicture[] = (resource.pins_preview ?? []).map(pin => ({
    id: pin.id,
    media: resolvePinMedia(pin),
    name: pin.file?.name ?? pin.url ?? '',
    mimeType: pin.file?.mime_type ?? (pin.url ? 'text/uri-list' : undefined)
  }));
  return {
    pictures,
    count: Math.max(resource.pin_count ?? 0, pictures.length)
  };
}

/**
 * A result's first pictures at the end of its row, as one button that opens them beside the list
 * at the one pressed, never picking the row: five squares where the row has the room, three in a
 * narrower one, under 480px only how many. Nothing without pictures (or `pins_preview`). The row is the
 * `@container/result` this reads its width from.
 */
function ResultPinStrip({
  resource,
  name,
  onOpen
}: {
  resource: Resource;
  name: string;
  onOpen: (pinId: number | undefined) => void;
}) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  const { pictures, count } = picturesOf(resource);
  if (pictures.length === 0) return null;
  const shown = pictures.slice(0, ROW_WIDE);
  const lastWide = Math.min(ROW_WIDE, shown.length) - 1;
  const lastNarrow = Math.min(ROW_NARROW, shown.length) - 1;
  return (
    <PictureStrip
      label={t('openPicturesOf', { count, name })}
      first={pictures[0]?.id}
      onOpen={onOpen}
      className="flex shrink-0 cursor-pointer items-center gap-1 rounded-md p-0.5 outline-none data-focus-visible:ring-2 data-focus-visible:ring-brand-500 data-hovered:bg-bg-subtle"
    >
      {shown.map((picture, index) => (
        <PictureStripSquare
          key={picture.id}
          picture={picture}
          className={cn(
            'size-[32px] shrink-0 @max-[480px]/result:hidden @min-[760px]/result:size-[40px]',
            index >= ROW_NARROW && '@max-[760px]/result:hidden'
          )}
        >
          {index === lastNarrow && index === lastWide ? (
            <MediaMoreOverlay
              count={count - shown.length}
              label={t('morePicturesLabel', { count: count - shown.length })}
            />
          ) : (
            <>
              {index === lastNarrow && (
                <span className="contents @min-[760px]/result:hidden">
                  <MediaMoreOverlay
                    count={count - ROW_NARROW}
                    label={t('morePicturesLabel', {
                      count: count - ROW_NARROW
                    })}
                  />
                </span>
              )}
              {index === lastWide && (
                <MediaMoreOverlay
                  count={count - shown.length}
                  label={t('morePicturesLabel', {
                    count: count - shown.length
                  })}
                />
              )}
            </>
          )}
        </PictureStripSquare>
      ))}
      <span className="inline-flex h-6 items-center gap-1 rounded-sm bg-bg-subtle px-2 text-xs font-semibold text-fg-default @min-[480px]/result:hidden">
        <Image className="size-3.5" aria-hidden />
        {count}
      </span>
    </PictureStrip>
  );
}

/** Where a row's squares will be while the row is read. */
function ResultPinStripSkeleton() {
  return (
    <span className="flex shrink-0 gap-1 p-0.5" aria-hidden>
      {Array.from({ length: ROW_WIDE }, (_, index) => (
        <Skeleton
          key={index}
          className={cn(
            'size-[32px] rounded-sm @max-[480px]/result:hidden @min-[760px]/result:size-[40px]',
            index >= ROW_NARROW && '@max-[760px]/result:hidden'
          )}
        />
      ))}
    </span>
  );
}

/**
 * A tile's pictures: the profile picture over a strip of the first pins (without one, the first
 * pin is the picture and the strip goes on from the second), "+N" on the strip's last. The strip
 * is one button that opens the pins beside the list at the one pressed; hovering a square shows
 * it in the picture. Without pins, the profile picture over an empty place of the strip's height,
 * without that too, the initials over a quiet line; without `pins_preview` (not asked of the
 * search), the picture alone.
 * `children` lie over the picture (the check, the host's marks).
 */
function ResultTilePictures({
  resource,
  name,
  onOpen,
  children
}: {
  resource: Resource;
  name: string;
  onOpen: (pinId: number | undefined) => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  const { pictures, count } = picturesOf(resource);
  const [scrubbed, setScrubbed] = useState<number | null>(null);
  const cover = resource.profile_picture ? null : (pictures[0] ?? null);
  const start = cover ? 1 : 0;
  const strip = pictures.slice(start, start + TILE_STRIP);
  const shownInCover = scrubbed === null ? null : (strip[scrubbed] ?? null);
  return (
    <>
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-md bg-bg-subtle">
        {resource.profile_picture ? (
          <ImagePreview
            src={resource.profile_picture}
            alt=""
            noUpscale
            className="size-full"
          />
        ) : cover ? (
          <PictureStripMedia picture={cover} large />
        ) : (
          <div className="grid size-full place-items-center text-2xl font-semibold text-fg-muted">
            {resourceRow.initials(resource)}
          </div>
        )}
        {shownInCover && (
          <span className="absolute inset-0">
            <PictureStripMedia
              key={shownInCover.id}
              picture={shownInCover}
              large
            />
            <span
              className={cn(
                buttonVariants({
                  variant: 'overlay',
                  size: 'xs',
                  shape: 'rounded'
                }),
                'pointer-events-none absolute right-1.5 bottom-1.5'
              )}
            >
              {t('picturePosition', {
                index: start + (scrubbed ?? 0) + 1,
                count
              })}
            </span>
          </span>
        )}
        {children}
      </div>
      {/* the search didn't bring the pins (the user doesn't show pictures): the picture alone */}
      {resource.pins_preview !== undefined && (
        <div className="relative grid shrink-0 grid-cols-4 gap-1">
          {strip.length > 0 ? (
            <PictureStrip
              label={t('openPicturesOf', { count, name })}
              first={strip[0]?.id}
              onOpen={onOpen}
              onHoverEnd={() => setScrubbed(null)}
              className="col-span-4 grid cursor-pointer grid-cols-4 gap-1 rounded-sm outline-none data-focus-visible:ring-2 data-focus-visible:ring-brand-500 data-focus-visible:ring-offset-2"
            >
              {strip.map((picture, index) => (
                <PictureStripSquare
                  key={picture.id}
                  picture={picture}
                  onPointerEnter={event => {
                    // a tap fires it too: the press opens the pictures, no preview
                    if (event.pointerType !== 'touch') setScrubbed(index);
                  }}
                  className={cn(
                    'aspect-square',
                    scrubbed === index &&
                      'ring-2 ring-brand-500 ring-offset-1 ring-offset-bg-default'
                  )}
                >
                  {index === strip.length - 1 && (
                    <MediaMoreOverlay
                      count={count - start - strip.length}
                      label={t('morePicturesLabel', {
                        count: count - start - strip.length
                      })}
                    />
                  )}
                </PictureStripSquare>
              ))}
            </PictureStrip>
          ) : (
            <>
              {/* one invisible square holds the strip's height */}
              <span aria-hidden className="aspect-square" />
              {/* a profile picture and no pins: the picture says it, no line under it */}
              {!resource.profile_picture && (
                <span className="absolute inset-0 flex items-center gap-1.5 text-xs text-fg-muted">
                  {count === 0 ? t('noPicturesYet') : t('onlyThisPicture')}
                </span>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

/** Where a tile's strip will be while the tile is read. */
function ResultTileStripSkeleton() {
  return (
    <div className="grid shrink-0 grid-cols-4 gap-1" aria-hidden>
      {Array.from({ length: TILE_STRIP }, (_, index) => (
        <Skeleton key={index} className="aspect-square rounded-sm" />
      ))}
    </div>
  );
}

/** A result's first pins in the picker: the row's strip and the tile's pictures, with their skeletons. */
export const ResultPictures = {
  Strip: ResultPinStrip,
  StripSkeleton: ResultPinStripSkeleton,
  Tile: ResultTilePictures,
  TileStripSkeleton: ResultTileStripSkeleton
};
